'use strict';

export class SignUp {
    static #GOAL_FIELD_CONFIGS = [
        {
            wrapperSelector: '.fundraising_campaign_goal_select_wrapper',
            selectName: 'fundraising_campaign_goal',
            otherAmountName: 'fundraising_campaign_goal_other_amount',
            otherNameName: 'fundraising_campaign_goal_other_name',
            otherWrapperSelector: '.fundraising_campaign_other_amount_wrapper',
        },
        {
            wrapperSelector: '.form-group.campaign-goal',
            selectName: 'teams_goal',
            otherAmountName: 'team_campaign_goal_other_amount',
            otherNameName: 'team_campaign_goal_other_name',
            otherWrapperSelector: '.fundraising_campaign_other_amount_wrapper',
        },
    ];

    constructor() {
        this.forms = Array.from(document.querySelectorAll('.signup-form-wrapper'));
        this.form = this.forms[0] || null;
        this._goalSyncBound = false;
        this._goalButtonsSyncTimer = null;
        this._goalButtonsUpdating = false;

        this.#initWalkAndFundraiseToggle();
        this.#initGoalButtons();
        this.#bindGoalSyncTriggers();
        this.#watchSignupDomChanges();
        this.#watchSignupStepChanges();

        if (this.form) {
            this.#initTeamSearch();
        }
    }

    #watchSignupDomChanges() {
        if (!document.body || typeof MutationObserver === 'undefined') {
            return;
        }

        const observer = new MutationObserver(() => {
            this.#initWalkAndFundraiseToggle();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    #watchSignupStepChanges() {
        const multiStep = document.querySelector('.levcharity-multi-step-form-wrapper');
        if (!multiStep || typeof MutationObserver === 'undefined') {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            const stepNavigated = mutations.some((mutation) => {
                if (mutation.type !== 'attributes') {
                    return false;
                }

                const target = mutation.target;
                return (
                    target instanceof HTMLElement
                    && target.classList.contains('signup-form-wrapper')
                    && (mutation.attributeName === 'class' || mutation.attributeName === 'style')
                );
            });

            if (stepNavigated) {
                this.#scheduleGoalButtonsSync();
            }
        });

        observer.observe(multiStep, {
            attributes: true,
            attributeFilter: ['class', 'style'],
            subtree: true,
        });
    }

    #scheduleGoalButtonsSync() {
        if (this._goalButtonsSyncTimer) {
            window.clearTimeout(this._goalButtonsSyncTimer);
        }

        this._goalButtonsSyncTimer = window.setTimeout(() => {
            this._goalButtonsSyncTimer = null;
            this.#initGoalButtons();
        }, 80);

        window.setTimeout(() => {
            if (!this._goalButtonsUpdating) {
                this.#initGoalButtons();
            }
        }, 320);
    }

    #initWalkAndFundraiseToggle() {
        this.forms.forEach((wrapper) => {
            const memberToggle = wrapper.querySelector('.member-mode-toggle input[name="member_mode"]');
            const fundraiseToggle = wrapper.querySelector('.fundraise-mode-toggle input[name="fundraise_mode"]');

            if (!memberToggle || !fundraiseToggle) {
                return;
            }

            if (wrapper.querySelector('.walk-fundraise-mode-toggle input[name="walk_fundraise_mode"]')) {
                return;
            }

            const toggleNode = this.#buildWalkAndFundraiseToggle();
            const memberGroup = memberToggle.closest('.member-mode-toggle');

            if (!memberGroup || !memberGroup.parentNode) {
                return;
            }

            memberGroup.parentNode.insertBefore(toggleNode, memberGroup);
            const masterToggle = toggleNode.querySelector('input[name="walk_fundraise_mode"]');

            if (!(masterToggle instanceof HTMLInputElement)) {
                return;
            }

            const setChecked = (field, checked) => {
                if (!(field instanceof HTMLInputElement) || field.checked === checked) {
                    return;
                }

                field.checked = checked;
                field.dispatchEvent(new Event('change', {bubbles: true}));
            };

            const syncMasterFromChildren = () => {
                masterToggle.checked = memberToggle.checked && fundraiseToggle.checked;
            };

            masterToggle.addEventListener('change', () => {
                if (masterToggle.checked) {
                    setChecked(memberToggle, true);
                    setChecked(fundraiseToggle, true);
                } else {
                    setChecked(memberToggle, false);
                    setChecked(fundraiseToggle, false);
                }

                syncMasterFromChildren();
            });

            memberToggle.addEventListener('change', syncMasterFromChildren);
            fundraiseToggle.addEventListener('change', syncMasterFromChildren);

            syncMasterFromChildren();
        });
    }

    #isActiveSignupStep(step) {
        if (!(step instanceof HTMLElement)) {
            return false;
        }

        if (step.classList.contains('current-step')) {
            return true;
        }

        const multiStep = step.closest('.levcharity-multi-step-form-wrapper');
        if (multiStep?.querySelector('.signup-form-wrapper.current-step')) {
            return false;
        }

        if (step.style.display === 'none') {
            return false;
        }

        const computed = window.getComputedStyle(step);
        return computed.display !== 'none' && computed.visibility !== 'hidden';
    }

    #shouldEnhanceGoalSelect(select) {
        if (!(select instanceof HTMLSelectElement)) {
            return false;
        }

        if (select.closest('.review-wrapper, .edit_teams_goal_wrapper, .edit_fundraising_goal_wrapper')) {
            return false;
        }

        const step = select.closest('.signup-form-wrapper');
        if (!step) {
            return true;
        }

        return this.#isActiveSignupStep(step);
    }

    #prepareGoalSelect(select) {
        const choicesInstance = select.choices_instance || select.choicesInstance;
        if (choicesInstance && typeof choicesInstance.destroy === 'function') {
            choicesInstance.destroy();
        }

        delete select.choices_instance;
        delete select.choicesInstance;
        select.classList.remove('choices__input');
        select.removeAttribute('hidden');
        select.style.removeProperty('display');
    }

    #ensureGoalWrapperObserver(wrapper, config) {
        if (!(wrapper instanceof HTMLElement) || wrapper.dataset.w4fGoalObserver === '1') {
            return;
        }

        wrapper.dataset.w4fGoalObserver = '1';
        const observer = new MutationObserver((mutations) => {
            if (!wrapper.classList.contains('w4f-fundraising-goal-enhanced')) {
                return;
            }

            const hasExternalChange = mutations.some((mutation) => {
                return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
                    if (!(node instanceof HTMLElement)) {
                        return false;
                    }

                    return (
                        !node.classList.contains('w4f-fundraising-goal-buttons')
                        && !node.classList.contains('w4f-fundraising-goal-button')
                        && !node.closest('.w4f-fundraising-goal-buttons')
                    );
                });
            });

            if (!hasExternalChange) {
                return;
            }

            this.#hideGoalSelectUi(wrapper);
            this.#syncGoalButtonState(wrapper, config.selectName);
        });

        observer.observe(wrapper, {childList: true, subtree: true});
    }

    #initGoalButtons() {
        if (this._goalButtonsUpdating) {
            return;
        }

        this._goalButtonsUpdating = true;

        try {
            SignUp.#GOAL_FIELD_CONFIGS.forEach((config) => {
                document.querySelectorAll(`select[name="${config.selectName}"]`).forEach((select) => {
                    if (!this.#shouldEnhanceGoalSelect(select)) {
                        return;
                    }

                    const wrapper = select.closest(config.wrapperSelector);
                    if (!(wrapper instanceof HTMLElement)) {
                        return;
                    }

                    this.#prepareGoalSelect(select);

                    const options = Array.from(select.options).filter((option) => option.value && !option.disabled);
                    const buttonOptions = options.filter((option) => option.value !== 'other');
                    if (!buttonOptions.length) {
                        return;
                    }

                    const existingButtons = wrapper.querySelector('.w4f-fundraising-goal-buttons');
                    const existingCount = existingButtons?.querySelectorAll('.w4f-fundraising-goal-button').length ?? 0;

                    if (
                        wrapper.classList.contains('w4f-fundraising-goal-enhanced')
                        && existingButtons
                        && existingCount === buttonOptions.length
                    ) {
                        this.#hideGoalSelectUi(wrapper);
                        this.#syncGoalButtonState(wrapper, config.selectName);
                        this.#forceOtherAmountVisible(wrapper, config);
                        return;
                    }

                    wrapper.querySelectorAll('.w4f-fundraising-goal-buttons').forEach((node) => node.remove());

                    const buttons = document.createElement('div');
                    buttons.className = 'w4f-fundraising-goal-buttons';
                    buttons.style.setProperty('--w4f-goal-btn-count', String(buttonOptions.length));

                    buttonOptions.forEach((option, index) => {
                        const button = document.createElement('button');
                        const colorIndex = (index % 5) + 1;
                        button.type = 'button';
                        button.className = `w4f-fundraising-goal-button goal-color-${colorIndex}`;
                        button.dataset.value = option.value;
                        const buttonLabel = this.#getGoalButtonLabel(option);
                        button.innerHTML = `
                    <span class="w4f-fundraising-goal-button__icon" aria-hidden="true">${this.#goalButtonIconSvg(index)}</span>
                    <span class="w4f-fundraising-goal-button__label">${buttonLabel}</span>
                `;
                        button.addEventListener('click', (event) => {
                            event.preventDefault();
                            select.value = button.dataset.value || '';
                            select.dispatchEvent(new Event('change', {bubbles: true}));
                            this.#syncGoalButtonState(wrapper, config.selectName);
                            this.#forceOtherAmountVisible(wrapper, config);
                        });
                        buttons.appendChild(button);
                    });

                    wrapper.classList.add('w4f-fundraising-goal-enhanced');

                    const insertBeforeNode = wrapper.querySelector('.choices') || select;
                    wrapper.insertBefore(buttons, insertBeforeNode);

                    if (select.dataset.w4fGoalChangeBound !== '1') {
                        select.dataset.w4fGoalChangeBound = '1';
                        select.addEventListener('change', () => {
                            this.#syncGoalButtonState(wrapper, config.selectName);
                            this.#forceOtherAmountVisible(wrapper, config);
                        });
                    }

                    this.#ensureGoalWrapperObserver(wrapper, config);
                    this.#forceOtherAmountVisible(wrapper, config);
                    this.#syncGoalButtonState(wrapper, config.selectName);
                    this.#hideGoalSelectUi(wrapper);
                });
            });
        } finally {
            this._goalButtonsUpdating = false;
        }
    }

    #syncGoalButtonState(wrapper, selectName) {
        if (!(wrapper instanceof HTMLElement)) {
            return;
        }

        const select = wrapper.querySelector(`select[name="${selectName}"]`);
        const buttons = wrapper.querySelectorAll('.w4f-fundraising-goal-button');
        if (!(select instanceof HTMLSelectElement) || !buttons.length) {
            return;
        }

        const selectedValue = select.value;
        buttons.forEach((button) => {
            if (!(button instanceof HTMLButtonElement)) {
                return;
            }
            button.classList.toggle('is-active', button.dataset.value === selectedValue);
        });
    }

    #bindGoalSyncTriggers() {
        if (this._goalSyncBound) {
            return;
        }
        this._goalSyncBound = true;

        document.addEventListener('click', (event) => {
            const target = event.target;
            if (!(target instanceof HTMLElement)) {
                return;
            }
            if (
                target.closest(
                    'button.submit-form, button.edit-button, button.button-prev-step, button[name="action"][value="next-step"], .form-header .breadcrumbs .breadcrumb-label'
                )
            ) {
                this.#scheduleGoalButtonsSync();
            }
        });
    }

    #forceOtherAmountVisible(wrapper, config) {
        if (!(wrapper instanceof HTMLElement)) {
            return;
        }

        const otherWrapper = wrapper.querySelector(config.otherWrapperSelector);
        if (otherWrapper instanceof HTMLElement) {
            otherWrapper.classList.remove('hidden');
            otherWrapper.style.display = '';
        }

        const otherNameInput = wrapper.querySelector(`input[name="${config.otherNameName}"]`);
        if (otherNameInput instanceof HTMLInputElement) {
            otherNameInput.disabled = true;
            otherNameInput.removeAttribute('required');
            otherNameInput.value = '';
            const otherNameGroup = otherNameInput.closest('.form-group');
            if (otherNameGroup instanceof HTMLElement) {
                otherNameGroup.style.display = 'none';
            } else {
                otherNameInput.style.display = 'none';
            }
        }

        const otherAmountInput = wrapper.querySelector(`input[name="${config.otherAmountName}"]`);
        if (!(otherAmountInput instanceof HTMLInputElement)) {
            return;
        }

        const linkedSelect = wrapper.querySelector(`select[name="${config.selectName}"]`);
        const isOtherSelected =
            linkedSelect instanceof HTMLSelectElement && linkedSelect.value === 'other';

        if (otherAmountInput.disabled) {
            otherAmountInput.disabled = false;
        }
        otherAmountInput.removeAttribute('required');

        if (!isOtherSelected) {
            otherAmountInput.value = '';
            delete otherAmountInput.dataset.w4fLastValue;
        } else if (!otherAmountInput.value && otherAmountInput.dataset.w4fLastValue) {
            otherAmountInput.value = otherAmountInput.dataset.w4fLastValue;
        }

        if (otherAmountInput.dataset.w4fBound !== '1') {
            otherAmountInput.dataset.w4fBound = '1';
            otherAmountInput.addEventListener('input', () => {
                const activeSelect = wrapper.querySelector(`select[name="${config.selectName}"]`);
                if (
                    activeSelect instanceof HTMLSelectElement
                    && otherAmountInput.value.trim() !== ''
                    && activeSelect.value !== 'other'
                    && Array.from(activeSelect.options).some((option) => option.value === 'other')
                ) {
                    otherAmountInput.dataset.w4fLastValue = otherAmountInput.value;
                    activeSelect.value = 'other';
                    activeSelect.dispatchEvent(new Event('change', {bubbles: true}));
                    return;
                }

                if (activeSelect instanceof HTMLSelectElement && activeSelect.value === 'other') {
                    otherAmountInput.dataset.w4fLastValue = otherAmountInput.value;
                }
            });
        }
    }

    #goalButtonIconSvg(index = 0) {
        const svgs = [
            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<g clip-path="url(#clip0_13399_477)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5895 2.90987C11.8865 2.73838 12.2525 2.73838 12.5495 2.90986L19.9637 7.19047C20.2607 7.36195 20.4437 7.67887 20.4437 8.02186V16.583C20.4437 16.926 20.2607 17.2429 19.9637 17.4144L12.5495 21.695C12.2525 21.8665 11.8865 21.8665 11.5895 21.695L4.17531 17.4144C3.87828 17.2429 3.69531 16.926 3.69531 16.583V8.02186C3.69531 7.67887 3.87828 7.36195 4.17531 7.19047L11.5895 2.90987Z" fill="#53427B"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5895 2.43721C11.8865 2.26572 12.2525 2.26572 12.5495 2.43721L19.9637 6.71779C20.2608 6.8893 20.4437 7.20622 20.4437 7.54918V16.1104C20.4437 16.4534 20.2608 16.7703 19.9637 16.9418L12.5495 21.2224C12.2525 21.3939 11.8865 21.3939 11.5895 21.2224L4.17531 16.9418C3.87828 16.7703 3.69531 16.4534 3.69531 16.1104V7.54918C3.69531 7.20622 3.87828 6.8893 4.17531 6.71779L11.5895 2.43721Z" fill="#8A6DCC"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5672 5.38059C11.8642 5.20908 12.2302 5.20908 12.5272 5.38059L17.3927 8.1897C17.6898 8.36118 17.8727 8.6781 17.8727 9.02109V14.6393C17.8727 14.9823 17.6898 15.2992 17.3927 15.4707L12.5272 18.2798C12.2302 18.4513 11.8642 18.4513 11.5672 18.2798L6.70168 15.4707C6.40465 15.2992 6.22168 14.9823 6.22168 14.6393V9.02109C6.22168 8.6781 6.40465 8.36118 6.70168 8.1897L11.5672 5.38059Z" fill="#F1EEF9"/>
<g filter="url(#filter0_d_13399_477)">
<path d="M10.9236 10.6875L11.9958 9.12305L13.0679 10.6875L14.8871 11.2237L13.7306 12.7268L13.7827 14.6226L11.9958 13.9872L10.2089 14.6226L10.261 12.7268L9.10449 11.2237L10.9236 10.6875Z" fill="white"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9953 8.28223C12.2727 8.28223 12.5322 8.41897 12.6889 8.64772L13.5921 9.9655L15.1244 10.4172C15.3904 10.4956 15.6007 10.7001 15.6864 10.9639C15.772 11.2277 15.7222 11.5167 15.5531 11.7365L14.5789 13.0026L14.6228 14.5996C14.6304 14.8768 14.5009 15.1399 14.2765 15.3029C14.0521 15.4659 13.7619 15.5078 13.5006 15.4149L11.9953 14.8797L10.4901 15.4149C10.2289 15.5078 9.93855 15.4659 9.71418 15.3029C9.48984 15.1399 9.36026 14.8768 9.36788 14.5996L9.4118 13.0026L8.43762 11.7365C8.26851 11.5167 8.21865 11.2277 8.30436 10.9639C8.39004 10.7001 8.60028 10.4956 8.86629 10.4172L10.3986 9.9655L11.3017 8.64772C11.4585 8.41897 11.718 8.28223 11.9953 8.28223ZM10.9232 10.6875L9.10403 11.2237L10.2606 12.7268L10.2084 14.6227L11.9953 13.9872L13.7823 14.6227L13.7301 12.7268L14.8867 11.2237L13.0675 10.6875L11.9953 9.12307L10.9232 10.6875Z" fill="white"/>
<g filter="url(#filter1_i_13399_477)">
<path d="M10.9236 10.6875L11.9958 9.12305L13.0679 10.6875L14.8871 11.2237L13.7306 12.7268L13.7827 14.6226L11.9958 13.9872L10.2089 14.6226L10.261 12.7268L9.10449 11.2237L10.9236 10.6875Z" fill="#8B6ECD"/>
</g>
</g>
<defs>
<filter id="filter0_d_13399_477" x="1.99337" y="5.56749" width="20.005" height="19.7222" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="3.55556"/>
<feGaussianBlur stdDeviation="3.55556"/>
<feColorMatrix type="matrix" values="0 0 0 0 0.596078 0 0 0 0 0.745098 0 0 0 0 0.780392 0 0 0 0.5 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_13399_477"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_13399_477" result="shape"/>
</filter>
<filter id="filter1_i_13399_477" x="9.10449" y="9.12305" width="5.78271" height="9.5" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="4"/>
<feGaussianBlur stdDeviation="2"/>
<feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
<feBlend mode="normal" in2="shape" result="effect1_innerShadow_13399_477"/>
</filter>
<clipPath id="clip0_13399_477">
<rect width="24" height="24" fill="white"/>
</clipPath>
</defs>
</svg>`,
            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<mask id="mask0_13399_1094" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
<path d="M24 0H0V24H24V0Z" fill="white"/>
</mask>
<g mask="url(#mask0_13399_1094)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.65283 11.3846C8.65283 10.7218 9.19007 10.1846 9.85283 10.1846H14.1283C14.791 10.1846 15.3283 10.7218 15.3283 11.3846V23.6299C15.3283 23.9349 15.002 24.1302 14.7313 23.9871L12.5514 22.8345C12.2005 22.649 11.7805 22.649 11.4296 22.8345L9.2498 23.9871C8.97917 24.1302 8.65283 23.9349 8.65283 23.6299V11.3846Z" fill="#1E2F53"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.65332 10.8006C8.65332 10.1379 9.19056 9.60059 9.85332 9.60059H14.1288C14.7916 9.60059 15.3288 10.1379 15.3288 10.8006V23.0459C15.3288 23.351 15.0024 23.5461 14.7318 23.4031L12.5519 22.2505C12.201 22.065 11.781 22.065 11.4301 22.2505L9.25029 23.4031C8.97963 23.5461 8.65332 23.351 8.65332 23.0459V10.8006Z" fill="#3B5998"/>
<mask id="mask1_13399_1094" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="8" y="9" width="8" height="15">
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.64014 10.8006C8.64014 10.1379 9.17741 9.60059 9.84014 9.60059H14.1156C14.7783 9.60059 15.3156 10.1379 15.3156 10.8006V23.0459C15.3156 23.351 14.9893 23.5461 14.7186 23.4031L12.5388 22.2505C12.1878 22.065 11.7679 22.065 11.417 22.2505L9.23714 23.4031C8.96648 23.5461 8.64014 23.351 8.64014 23.0459V10.8006Z" fill="white"/>
</mask>
<g mask="url(#mask1_13399_1094)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M10.3223 16.2441H13.66V23.7184H10.3223V16.2441Z" fill="white"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1757 1.38378C11.6786 1.09343 12.2983 1.09343 12.8011 1.38378L19.2696 5.11838C19.7726 5.40872 20.0824 5.9453 20.0824 6.52601V13.9951C20.0824 14.5759 19.7726 15.1125 19.2696 15.4028L12.8011 19.1374C12.2983 19.4278 11.6786 19.4278 11.1757 19.1374L4.70723 15.4028C4.20434 15.1125 3.89453 14.5759 3.89453 13.9951V6.52601C3.89453 5.9453 4.20434 5.40872 4.70723 5.11838L11.1757 1.38378Z" fill="#1E2F53"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1757 0.926746C11.6786 0.636397 12.2983 0.636397 12.8011 0.926746L19.2696 4.66135C19.7726 4.95169 20.0824 5.48827 20.0824 6.06898V13.5381C20.0824 14.1189 19.7726 14.6555 19.2696 14.9458L12.8011 18.6804C12.2983 18.9708 11.6786 18.9708 11.1757 18.6804L4.70723 14.9458C4.20434 14.6555 3.89453 14.1189 3.89453 13.5381V6.06898C3.89453 5.48827 4.20434 4.95169 4.70723 4.66135L11.1757 0.926746Z" fill="#3B5998"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.4465 5.41082L12.6948 2.08621C12.3136 1.86579 11.8438 1.86579 11.4624 2.08621L5.71074 5.41082C5.32941 5.63126 5.09448 6.0386 5.09448 6.47945V8.51489C5.09448 8.75156 5.02449 8.98298 4.8933 9.17999L4.62819 9.57812C4.53735 9.71453 4.53735 9.89216 4.62819 10.0286L4.8933 10.4267C5.02449 10.6237 5.09448 10.8551 5.09448 11.0918V13.1287C5.09448 13.5696 5.32941 13.9769 5.71074 14.1974L11.4624 17.5219C11.8438 17.7424 12.3136 17.7424 12.6948 17.5219L18.4465 14.1974C18.8278 13.9769 19.0627 13.5696 19.0627 13.1287V11.0918C19.0627 10.8551 19.1327 10.6237 19.2639 10.4266L19.529 10.0286C19.6198 9.89216 19.6198 9.71453 19.529 9.57812L19.2639 9.18005C19.1327 8.98304 19.0627 8.75162 19.0627 8.51492V6.47945C19.0627 6.0386 18.8278 5.63126 18.4465 5.41082ZM5.70648 10.4267C5.57529 10.6237 5.5053 10.8551 5.5053 11.0918V13.1287C5.5053 13.4226 5.6619 13.6942 5.91612 13.8411L11.6677 17.1657C11.922 17.3127 12.2352 17.3127 12.4894 17.1657L18.2411 13.8411C18.4953 13.6942 18.6519 13.4226 18.6519 13.1287V11.0918C18.6519 10.8551 18.5819 10.6237 18.4507 10.4267L18.1856 10.0286C18.0948 9.89216 18.0948 9.71453 18.1856 9.57812L18.4507 9.17999C18.5819 8.98298 18.6519 8.75159 18.6519 8.51489V6.47945C18.6519 6.18557 18.4953 5.91398 18.2411 5.76704L12.4894 2.44242C12.2352 2.29547 11.922 2.29547 11.6677 2.44242L5.91612 5.76704C5.6619 5.91398 5.5053 6.18557 5.5053 6.47945V8.51492C5.5053 8.75159 5.57529 8.98301 5.70648 9.18002L5.97159 9.57812C6.0624 9.71453 6.0624 9.89216 5.97159 10.0286L5.70648 10.4267Z" fill="#1E2F53"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5314 3.56585C11.8142 3.40253 12.1627 3.40253 12.4456 3.56585L17.1619 6.28877C17.4447 6.45212 17.619 6.75395 17.619 7.08059V12.5264C17.619 12.8531 17.4447 13.155 17.1619 13.3183L12.4456 16.0411C12.1627 16.2045 11.8142 16.2045 11.5314 16.0411L6.81505 13.3183C6.53218 13.155 6.35791 12.8531 6.35791 12.5264V7.08059C6.35791 6.75395 6.53218 6.45212 6.81505 6.28877L11.5314 3.56585Z" fill="url(#paint0_linear_13399_1094)"/>
<path d="M10.9521 8.37726L11.9884 6.86523L13.0246 8.37726L14.7829 8.89557L13.6651 10.3484L13.7155 12.1808L11.9884 11.5666L10.2613 12.1808L10.3117 10.3484L9.19385 8.89557L10.9521 8.37726Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9886 6.05273C12.2566 6.05273 12.5075 6.18491 12.659 6.40601L13.5318 7.67966L15.013 8.11625C15.27 8.19203 15.4732 8.38973 15.5561 8.64464C15.6389 8.89958 15.5907 9.17894 15.4272 9.39137L14.4857 10.6151L14.5282 12.1587C14.5355 12.4266 14.4102 12.681 14.1934 12.8384C13.9765 12.9961 13.6959 13.0365 13.4435 12.9467L11.9886 12.4293L10.5338 12.9467C10.2812 13.0365 10.0006 12.9961 9.78379 12.8384C9.56695 12.681 9.4417 12.4266 9.44908 12.1587L9.49153 10.6151L8.54995 9.39137C8.38651 9.17894 8.3383 8.89958 8.42113 8.64464C8.50396 8.38973 8.70718 8.19203 8.96425 8.11625L10.4453 7.67966L11.3182 6.40601C11.4698 6.18491 11.7205 6.05273 11.9886 6.05273ZM10.9523 8.37746L9.19405 8.89577L10.3119 10.3486L10.2615 12.181L11.9886 11.5668L13.7157 12.181L13.6653 10.3486L14.7831 8.89577L13.0248 8.37746L11.9886 6.86543L10.9523 8.37746Z" fill="white"/>
<path d="M10.9521 8.37726L11.9884 6.86523L13.0246 8.37726L14.7829 8.89557L13.6651 10.3484L13.7155 12.1808L11.9884 11.5666L10.2613 12.1808L10.3117 10.3484L9.19385 8.89557L10.9521 8.37726Z" fill="url(#paint1_linear_13399_1094)"/>
<path opacity="0.4" d="M11.9885 9.80358V6.86523L10.9385 8.35647L11.9885 9.80358Z" fill="url(#paint2_linear_13399_1094)"/>
<path opacity="0.8" d="M11.9883 9.80356L10.9383 8.35645L9.19385 8.89555L11.9883 9.80356Z" fill="url(#paint3_linear_13399_1094)"/>
<path opacity="0.5" d="M11.9884 9.80371L10.2881 10.3569L10.2612 12.1809L11.9884 9.80371Z" fill="url(#paint4_linear_13399_1094)"/>
<path opacity="0.4" d="M13.7154 12.1809L13.6885 10.3569L11.9883 9.80371L13.7154 12.1809Z" fill="url(#paint5_linear_13399_1094)"/>
<path d="M13.6885 10.3567L14.7828 8.89551L11.9883 9.80352L13.6885 10.3567Z" fill="#FEBA1B"/>
<path opacity="0.6" d="M14.7828 8.89555L13.0383 8.35645L11.9883 9.80356L14.7828 8.89555Z" fill="url(#paint6_linear_13399_1094)"/>
</g>
<defs>
<linearGradient id="paint0_linear_13399_1094" x1="11.9885" y1="3.29981" x2="11.9885" y2="16.3072" gradientUnits="userSpaceOnUse">
<stop stop-color="#EBEEF5"/>
<stop offset="1" stop-color="#C4CDE0"/>
</linearGradient>
<linearGradient id="paint1_linear_13399_1094" x1="9.05" y1="6.86523" x2="9.05" y2="12.7419" gradientUnits="userSpaceOnUse">
<stop stop-color="#FCA50E"/>
<stop offset="1" stop-color="#FFC823"/>
</linearGradient>
<linearGradient id="paint2_linear_13399_1094" x1="10.9385" y1="6.13065" x2="9.20094" y2="7.3725" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFEC36" stop-opacity="0.467505"/>
<stop offset="1" stop-color="#FFF7AB"/>
</linearGradient>
<linearGradient id="paint3_linear_13399_1094" x1="9.21152" y1="8.71366" x2="10.8489" y2="10.7292" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFED08" stop-opacity="0.8"/>
<stop offset="1" stop-color="white"/>
</linearGradient>
<linearGradient id="paint4_linear_13399_1094" x1="11.1248" y1="12.7752" x2="12.5125" y2="10.7587" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFED08" stop-opacity="0.01"/>
<stop offset="1" stop-color="#FFEF63"/>
</linearGradient>
<linearGradient id="paint5_linear_13399_1094" x1="11.9883" y1="9.20944" x2="10.6006" y2="11.2259" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFEC36" stop-opacity="0.467505"/>
<stop offset="1" stop-color="#FFE94A"/>
</linearGradient>
<linearGradient id="paint6_linear_13399_1094" x1="11.9883" y1="7.99468" x2="11.5493" y2="9.68992" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFEC36" stop-opacity="0.467505"/>
<stop offset="1" stop-color="#FFF7AB"/>
</linearGradient>
</defs>
</svg>`,
            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<g clip-path="url(#clip0_13399_1152)">
<mask id="mask0_13399_1152" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
<path d="M24 0H0V24H24V0Z" fill="white"/>
</mask>
<g mask="url(#mask0_13399_1152)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1826 9.04102H17.6842V21.3867C17.6842 21.6888 17.3664 21.8852 17.0961 21.7501L14.4335 20.4188L11.7707 21.7501C11.5006 21.8852 11.1826 21.6888 11.1826 21.3867V9.04102Z" fill="#467039"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.30615 9.04102H12.8078V21.3867C12.8078 21.6888 12.4898 21.8852 12.2197 21.7501L9.55695 20.4188L6.89424 21.7501C6.62406 21.8852 6.30615 21.6888 6.30615 21.3867V9.04102Z" fill="#467039"/>
<g filter="url(#filter0_d_13399_1152)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.30615 8.58398H12.8078V20.9297C12.8078 21.2317 12.4898 21.4282 12.2197 21.2931L9.55695 19.9618L6.89424 21.2931C6.62406 21.4282 6.30615 21.2317 6.30615 20.9297V8.58398Z" fill="#75BA5F"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1826 8.58398H17.6842V20.9297C17.6842 21.2317 17.3664 21.4282 17.0961 21.2931L14.4335 19.9618L11.7707 21.2931C11.5006 21.4282 11.1826 21.2317 11.1826 20.9297V8.58398Z" fill="#75BA5F"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.74463 11.0732H15.2462V23.419C15.2462 23.721 14.9284 23.9175 14.6581 23.7824L11.9955 22.451L9.33269 23.7824C9.06251 23.9175 8.74463 23.721 8.74463 23.419V11.0732Z" fill="#467039"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.74463 10.6152H15.2462V22.961C15.2462 23.263 14.9284 23.4595 14.6581 23.3244L11.9955 21.993L9.33269 23.3244C9.06251 23.4595 8.74463 23.263 8.74463 22.961V10.6152Z" fill="white"/>
<mask id="mask1_13399_1152" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="8" y="10" width="8" height="14">
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.74463 10.6152H15.2462V22.961C15.2462 23.263 14.9284 23.4595 14.6581 23.3244L11.9955 21.993L9.33269 23.3244C9.06251 23.4595 8.74463 23.263 8.74463 22.961V10.6152Z" fill="white"/>
</mask>
<g mask="url(#mask1_13399_1152)">
<path d="M13.6209 10.6152H10.3701V23.6184H13.6209V10.6152Z" fill="#75BA5F"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1826 1.3828C11.6855 1.09245 12.3051 1.09245 12.808 1.3828L19.2765 5.11741C19.7793 5.40775 20.0892 5.94433 20.0892 6.52504V13.9942C20.0892 14.575 19.7793 15.1115 19.2765 15.4018L12.808 19.1364C12.3051 19.4268 11.6855 19.4268 11.1826 19.1364L4.71407 15.4018C4.21118 15.1115 3.90137 14.575 3.90137 13.9942V6.52504C3.90137 5.94433 4.21118 5.40775 4.71407 5.11741L11.1826 1.3828Z" fill="#467039"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.3613 0.937489C11.8642 0.647137 12.4838 0.64714 12.9867 0.937489L19.4552 4.67208C19.9581 4.96242 20.2679 5.49903 20.2679 6.07971V13.5488C20.2679 14.1296 19.9581 14.6662 19.4552 14.9565L12.9867 18.6911C12.4838 18.9815 11.8642 18.9815 11.3613 18.6911L4.89278 14.9565C4.38989 14.6662 4.08008 14.1296 4.08008 13.5488V6.07971C4.08008 5.49903 4.38989 4.96242 4.89278 4.67208L11.3613 0.937489Z" fill="#75BA5F"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.4465 5.40984L12.6948 2.08524C12.3136 1.86482 11.8438 1.86482 11.4624 2.08524L5.71074 5.40984C5.32941 5.63028 5.09448 6.03762 5.09448 6.47847V8.87688L4.62819 9.57714C4.53735 9.71355 4.53735 9.89118 4.62819 10.0276L5.09448 10.7279V13.1277C5.09448 13.5686 5.32941 13.9759 5.71074 14.1964L11.4624 17.521C11.8438 17.7414 12.3136 17.7414 12.6948 17.521L18.4465 14.1964C18.8278 13.9759 19.0627 13.5686 19.0627 13.1277V10.7278L19.529 10.0276C19.6198 9.89118 19.6198 9.71355 19.529 9.57714L19.0627 8.87694V6.47847C19.0627 6.03762 18.8278 5.63028 18.4465 5.40984ZM5.5053 10.7278V13.1277C5.5053 13.4216 5.6619 13.6932 5.91612 13.8401L11.6677 17.1647C11.922 17.3117 12.2352 17.3117 12.4894 17.1647L18.2411 13.8401C18.4953 13.6932 18.6519 13.4216 18.6519 13.1277V10.7278L18.1856 10.0276C18.0948 9.89118 18.0948 9.71355 18.1856 9.57714L18.6519 8.87688V6.47847C18.6519 6.18459 18.4953 5.913 18.2411 5.76606L12.4894 2.44144C12.2352 2.2945 11.922 2.2945 11.6677 2.44144L5.91612 5.76606C5.6619 5.913 5.5053 6.18459 5.5053 6.47847V8.87691L5.97159 9.57714C6.0624 9.71355 6.0624 9.89118 5.97159 10.0276L5.5053 10.7278Z" fill="#5D964C"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.4137 3.56487C11.6965 3.40155 12.045 3.40155 12.328 3.56487L17.0442 6.28779C17.3271 6.45114 17.5013 6.75297 17.5013 7.07961V12.5254C17.5013 12.8522 17.3271 13.154 17.0442 13.3173L12.328 16.0402C12.045 16.2035 11.6965 16.2035 11.4137 16.0402L6.69737 13.3173C6.4145 13.154 6.24023 12.8522 6.24023 12.5254V7.07961C6.24023 6.75297 6.4145 6.45114 6.69737 6.28779L11.4137 3.56487Z" fill="url(#paint0_linear_13399_1152)"/>
<mask id="mask2_13399_1152" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="6" y="3" width="12" height="14">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.4136 3.56487C11.6965 3.40155 12.045 3.40155 12.3279 3.56487L17.0441 6.28779C17.3271 6.45114 17.5013 6.75297 17.5013 7.07961V12.5254C17.5013 12.8522 17.3271 13.154 17.0441 13.3173L12.3279 16.0402C12.045 16.2035 11.6965 16.2035 11.4136 16.0402L6.69737 13.3173C6.4145 13.154 6.24023 12.8522 6.24023 12.5254V7.07961C6.24023 6.75297 6.4145 6.45114 6.69737 6.28779L11.4136 3.56487Z" fill="url(#paint1_linear_13399_1152)"/>
</mask>
<g mask="url(#mask2_13399_1152)">
<g style="mix-blend-mode:multiply" opacity="0.3">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.7818 9.80235L13.4072 3.30078H10.1564L11.7818 9.80235ZM11.7818 9.80235L6.03522 6.35439L8.33388 4.05573L11.7818 9.80235ZM11.7818 9.80235L15.2298 4.05573L17.5286 6.35436L11.7818 9.80235L18.2834 8.17698V11.4278L11.7818 9.80235L17.5284 13.2504L15.2298 15.549L11.7818 9.80235ZM11.7818 9.80235L13.4072 16.304H10.1564L11.7818 9.80235ZM11.7818 9.80235L5.28027 8.17698V11.4278L11.7818 9.80235L6.03516 13.2504L8.33382 15.549L11.7818 9.80235Z" fill="#ACD69F"/>
</g>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9954 6.14551L15.2463 9.80266L11.9954 13.4598L8.74463 9.80266L11.9954 6.14551Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9951 5.33301C12.2273 5.33301 12.4483 5.43228 12.6026 5.6058L15.8533 9.26292C16.127 9.57084 16.127 10.0348 15.8533 10.3428L12.6026 13.9999C12.4483 14.1734 12.2273 14.2727 11.9951 14.2727C11.763 14.2727 11.5419 14.1734 11.3877 13.9999L8.13693 10.3428C7.86321 10.0348 7.86321 9.57084 8.13693 9.26292L11.3877 5.6058C11.5419 5.43228 11.763 5.33301 11.9951 5.33301ZM11.9951 6.14571L8.74434 9.80286L11.9951 13.46L15.2459 9.80286L11.9951 6.14571Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9954 6.14551L15.2463 9.80266H8.74463L11.9954 6.14551Z" fill="url(#paint2_linear_13399_1152)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9955 13.4599L15.2462 9.80273H8.74463L11.9955 13.4599Z" fill="url(#paint3_linear_13399_1152)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.995 6.14551L10.4277 9.80266H13.5625L11.995 6.14551Z" fill="url(#paint4_linear_13399_1152)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.995 13.4599L10.4277 9.80273H13.5625L11.995 13.4599Z" fill="url(#paint5_linear_13399_1152)"/>
</g>
</g>
<defs>
<filter id="filter0_d_13399_1152" x="-0.804968" y="8.58397" width="20.7237" height="26.9752" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="7.11111"/>
<feGaussianBlur stdDeviation="3.55556"/>
<feColorMatrix type="matrix" values="0 0 0 0 0.52549 0 0 0 0 0.756863 0 0 0 0 0.25098 0 0 0 0.2 0"/>
<feBlend mode="darken" in2="BackgroundImageFix" result="effect1_dropShadow_13399_1152"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_13399_1152" result="shape"/>
</filter>
<linearGradient id="paint0_linear_13399_1152" x1="11.8708" y1="3.30093" x2="11.8708" y2="16.3042" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="#ACD69F"/>
</linearGradient>
<linearGradient id="paint1_linear_13399_1152" x1="11.8708" y1="3.30093" x2="11.8708" y2="16.3042" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="#ACD69F"/>
</linearGradient>
<linearGradient id="paint2_linear_13399_1152" x1="8.74463" y1="6.14551" x2="8.74463" y2="9.80266" gradientUnits="userSpaceOnUse">
<stop stop-color="#27DEEB"/>
<stop offset="1" stop-color="#08C9FF"/>
</linearGradient>
<linearGradient id="paint3_linear_13399_1152" x1="8.74463" y1="9.80273" x2="8.74463" y2="13.4599" gradientUnits="userSpaceOnUse">
<stop stop-color="#009BF6"/>
<stop offset="1" stop-color="#00B5FF"/>
</linearGradient>
<linearGradient id="paint4_linear_13399_1152" x1="10.4277" y1="6.14551" x2="10.4277" y2="9.80266" gradientUnits="userSpaceOnUse">
<stop stop-color="#8AFFFF"/>
<stop offset="1" stop-color="#0DF3FF"/>
</linearGradient>
<linearGradient id="paint5_linear_13399_1152" x1="10.4277" y1="9.80273" x2="10.4277" y2="13.4599" gradientUnits="userSpaceOnUse">
<stop stop-color="#00BAF3"/>
<stop offset="1" stop-color="#00B5FF"/>
</linearGradient>
<clipPath id="clip0_13399_1152">
<rect width="24" height="24" fill="white"/>
</clipPath>
</defs>
</svg>`,
            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<g clip-path="url(#clip0_13399_1246)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.28564 9.05371H12.7872V21.3994C12.7872 21.7014 12.4694 21.8979 12.1992 21.7628L9.53644 20.4315L6.87372 21.7628C6.60352 21.8979 6.28564 21.7014 6.28564 21.3994V9.05371Z" fill="#AF711D"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1621 9.05371H17.6637V21.3994C17.6637 21.7014 17.3458 21.8979 17.0756 21.7628L14.4129 20.4315L11.7502 21.7628C11.4801 21.8979 11.1621 21.7014 11.1621 21.3994V9.05371Z" fill="#AF711D"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.28564 8.59668H12.7872V20.9424C12.7872 21.2444 12.4694 21.4409 12.1992 21.3058L9.53644 19.9745L6.87372 21.3058C6.60352 21.4409 6.28564 21.2444 6.28564 20.9424V8.59668Z" fill="#FAA229"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1621 8.59668H17.6637V20.9424C17.6637 21.2444 17.3458 21.4409 17.0756 21.3058L14.4129 19.9745L11.7502 21.3058C11.4801 21.4409 11.1621 21.2444 11.1621 20.9424V8.59668Z" fill="#FAA229"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.68555 11.0859H15.1871V23.4317C15.1871 23.7337 14.8692 23.9302 14.5991 23.7951L11.9363 22.4638L9.27362 23.7951C9.00345 23.9302 8.68555 23.7337 8.68555 23.4317V11.0859Z" fill="#AF711D"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.68555 10.6289H15.1871V22.9746C15.1871 23.2767 14.8692 23.4732 14.5991 23.3381L11.9363 22.0068L9.27362 23.3381C9.00345 23.4732 8.68555 23.2767 8.68555 22.9746V10.6289Z" fill="white"/>
<mask id="mask0_13399_1246" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="8" y="10" width="8" height="14">
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.68555 10.6289H15.1871V22.9746C15.1871 23.2767 14.8692 23.4732 14.5991 23.3381L11.9363 22.0068L9.27362 23.3381C9.00345 23.4732 8.68555 23.2767 8.68555 22.9746V10.6289Z" fill="white"/>
</mask>
<g mask="url(#mask0_13399_1246)">
<path d="M13.5999 10.6289H10.3491V23.6321H13.5999V10.6289Z" fill="#FAA229"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.04114 5.13046L3.00939 4.31777V3.9114L1.05826 3.09842C0.866377 3.01848 0.668866 3.19994 0.732315 3.39792L3.04683 10.6192C3.08706 10.7447 3.18572 10.8429 3.31143 10.8826L5.04114 11.4289V5.13046Z" fill="url(#paint0_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M1.13636 2.91043L3.08748 3.72341C3.1632 3.75495 3.21252 3.82894 3.21252 3.91095V4.17975L5.11653 4.94136C5.19369 4.97223 5.24426 5.04694 5.24426 5.13V11.4284C5.24426 11.4932 5.2134 11.554 5.1612 11.5923C5.10897 11.6306 5.04163 11.6417 4.9799 11.6222L3.2502 11.0759C3.06163 11.0164 2.91364 10.8691 2.85328 10.6807L0.538785 3.45948C0.422464 3.09655 0.784559 2.76384 1.13636 2.91043ZM0.925744 3.33545L3.24026 10.5567C3.26037 10.6195 3.30969 10.6686 3.37255 10.6884L4.83792 11.1512V5.26757L2.93388 4.50595C2.85674 4.47509 2.80617 4.40038 2.80617 4.31731V4.0464L0.980068 3.28553C0.948093 3.27221 0.91517 3.30245 0.925744 3.33545Z" fill="url(#paint1_linear_13399_1246)"/>
<mask id="mask1_13399_1246" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="2" width="6" height="10">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.05382 5.13046L3.0221 4.31777V3.9114L1.07096 3.09842C0.87907 3.01848 0.681562 3.19994 0.74501 3.39792L3.05952 10.6192C3.09977 10.7447 3.19841 10.8429 3.32412 10.8826L5.05382 11.4289V5.13046Z" fill="url(#paint2_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M1.14905 2.91043L3.10019 3.72341C3.17591 3.75495 3.22523 3.82894 3.22523 3.91095V4.17975L5.12924 4.94136C5.20638 4.97223 5.25695 5.04694 5.25695 5.13V11.4284C5.25695 11.4932 5.22611 11.554 5.17388 11.5923C5.12166 11.6306 5.05434 11.6417 4.99259 11.6222L3.26288 11.0759C3.07432 11.0164 2.92636 10.869 2.866 10.6807L0.551479 3.45948C0.435161 3.09655 0.797254 2.76384 1.14905 2.91043ZM0.938441 3.33545L3.25295 10.5567C3.27306 10.6195 3.32238 10.6686 3.38526 10.6884L4.8506 11.1512V5.26757L2.94659 4.50595C2.86945 4.47509 2.81886 4.40038 2.81886 4.31731V4.0464L0.992765 3.28553C0.960787 3.27221 0.927864 3.30245 0.938441 3.33545Z" fill="url(#paint3_linear_13399_1246)"/>
</mask>
<g mask="url(#mask1_13399_1246)">
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M5.12938 7.37938C5.23356 7.42107 5.28425 7.53932 5.24258 7.64348C5.2009 7.74767 5.08265 7.79835 4.97846 7.75669L2.94672 6.94398C2.86958 6.91314 2.81902 6.83842 2.81902 6.75534V6.48445L0.505944 5.52066C0.402365 5.4775 0.353384 5.35854 0.396541 5.25498C0.4397 5.15139 0.558653 5.10241 0.662232 5.14556L3.10032 6.16146C3.17604 6.19299 3.22536 6.26696 3.22536 6.34899V6.61777L5.12938 7.37938Z" fill="url(#paint4_linear_13399_1246)"/>
</g>
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M5.53564 9.81786C5.63982 9.85953 5.69048 9.97777 5.64882 10.082C5.60716 10.1862 5.48891 10.2368 5.38472 10.1951L3.35298 9.38245C3.27584 9.35159 3.22525 9.27688 3.22525 9.19381V8.9229L0.912192 7.95913C0.808615 7.91598 0.759634 7.79701 0.802791 7.69345C0.84595 7.58987 0.964903 7.54089 1.06848 7.58404L3.50658 8.59991C3.5823 8.63144 3.6316 8.70544 3.6316 8.78745V9.05625L5.53564 9.81786Z" fill="url(#paint5_linear_13399_1246)"/>
</g>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.8823 5.13046L20.9141 4.31777V3.9114L22.8653 3.09842C23.0572 3.01848 23.2547 3.19994 23.1913 3.39792L20.8767 10.6192C20.8365 10.7447 20.7378 10.8429 20.6121 10.8826L18.8823 11.4289V5.13046Z" fill="url(#paint6_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M22.9978 3.33547C23.0084 3.30242 22.9754 3.27223 22.9435 3.28553L21.1173 4.0464V4.31731C21.1173 4.4004 21.0667 4.47509 20.9896 4.50595L19.0855 5.26757V11.1512L20.5509 10.6884C20.6138 10.6686 20.6632 10.6194 20.6833 10.5567L22.9978 3.33547ZM22.7872 2.91043C23.139 2.76384 23.501 3.09658 23.3848 3.45946L21.0702 10.6807C21.0099 10.8691 20.8618 11.0164 20.6733 11.0759L18.9436 11.6222C18.8818 11.6417 18.8145 11.6306 18.7623 11.5923C18.71 11.554 18.6792 11.4932 18.6792 11.4284V5.13C18.6792 5.04694 18.7298 4.97222 18.8069 4.94136L20.711 4.17974V3.91097C20.711 3.82894 20.7603 3.75494 20.836 3.72341L22.7872 2.91043Z" fill="url(#paint7_linear_13399_1246)"/>
<mask id="mask2_13399_1246" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="16" y="1" width="9" height="13">
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.6299 5.13046L20.6617 4.31777V3.9114L22.6128 3.09842C22.8046 3.01848 23.0021 3.19994 22.9387 3.39792L20.6242 10.6192C20.5839 10.7447 20.4853 10.8429 20.3596 10.8826L18.6299 11.4289V5.13046Z" fill="url(#paint8_linear_13399_1246)" stroke="url(#paint9_linear_13399_1246)" stroke-width="3.55556" stroke-linecap="round" stroke-linejoin="round"/>
</mask>
<g mask="url(#mask2_13399_1246)">
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M18.7943 7.37938C18.69 7.42107 18.6394 7.53932 18.6811 7.64348C18.7227 7.74767 18.841 7.79835 18.9451 7.75669L20.9769 6.94398C21.054 6.91314 21.1047 6.83842 21.1047 6.75534V6.48445L23.4177 5.52066C23.5212 5.4775 23.5703 5.35854 23.5271 5.25498C23.484 5.15139 23.365 5.10241 23.2613 5.14556L20.8233 6.16146C20.7476 6.19299 20.6983 6.26696 20.6983 6.34899V6.61777L18.7943 7.37938Z" fill="url(#paint10_linear_13399_1246)"/>
</g>
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M18.388 9.81786C18.2839 9.85953 18.2331 9.97777 18.2749 10.082C18.3164 10.1862 18.4347 10.2368 18.539 10.1951L20.5706 9.38245C20.6478 9.35159 20.6984 9.27688 20.6984 9.19381V8.9229L23.0114 7.95913C23.1151 7.91598 23.164 7.79701 23.1209 7.69345C23.0777 7.58987 22.9587 7.54089 22.8552 7.58404L20.417 8.59991C20.3414 8.63144 20.292 8.70544 20.292 8.78745V9.05625L18.388 9.81786Z" fill="url(#paint11_linear_13399_1246)"/>
</g>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.6603 12.2412L3.2275 12.9575C3.10705 13.0179 3.02425 13.1339 3.00644 13.2674L2.69367 15.6132C2.66701 15.8132 2.88106 15.9572 3.05631 15.8571L5.47302 14.4762L4.6603 12.2412Z" fill="url(#paint12_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.73567 12.0525C4.7895 12.074 4.83162 12.1174 4.85142 12.1718L5.66411 14.4068C5.6979 14.4998 5.65986 14.6036 5.57397 14.6526L3.15726 16.0336C2.83605 16.2171 2.44353 15.9532 2.49244 15.5864L2.80521 13.2406C2.83192 13.0403 2.95612 12.8664 3.13667 12.7759L4.56961 12.0595C4.62143 12.0336 4.68186 12.0311 4.73567 12.0525ZM3.31861 13.1393C3.25828 13.1695 3.2169 13.2276 3.208 13.2943L2.89523 15.6401C2.89079 15.6733 2.92641 15.6974 2.95566 15.6807L5.22371 14.3848L4.54744 12.5249L3.31861 13.1393Z" fill="url(#paint13_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M19.2889 12.2412L20.7217 12.9575C20.8422 13.0179 20.925 13.1339 20.9428 13.2674L21.2555 15.6132C21.2822 15.8132 21.0682 15.9572 20.8929 15.8571L18.4762 14.4762L19.2889 12.2412Z" fill="url(#paint14_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M19.2135 12.0525C19.1597 12.074 19.1176 12.1174 19.0978 12.1718L18.2851 14.4068C18.2513 14.4998 18.2894 14.6036 18.3753 14.6526L20.792 16.0336C21.1132 16.2171 21.5057 15.9532 21.4568 15.5864L21.144 13.2406C21.1173 13.0403 20.9931 12.8664 20.8125 12.7759L19.3796 12.0595C19.3278 12.0336 19.2674 12.0311 19.2135 12.0525ZM20.6306 13.1393C20.6909 13.1695 20.7323 13.2276 20.7412 13.2943L21.054 15.6401C21.0584 15.6733 21.0228 15.6974 20.9936 15.6807L18.7255 14.3848L19.4018 12.5249L20.6306 13.1393Z" fill="url(#paint15_linear_13399_1246)"/>
<path d="M8.53874 6.07479L7.74259 6.45982C6.79816 6.91654 5.70117 6.22851 5.70117 5.17944V3.21163C5.70117 2.99386 5.81736 2.79264 6.00595 2.68378L9.037 0.933773C9.17246 0.855572 9.34178 0.953324 9.34178 1.10973V4.79443C9.34178 5.33991 9.0298 5.83731 8.53874 6.07479Z" fill="url(#paint16_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.13866 1.11008L6.10757 2.86008C5.98186 2.93266 5.90439 3.06682 5.90439 3.21199V5.1798C5.90439 6.07901 6.84469 6.66874 7.65418 6.27725L8.45034 5.89224C8.87125 5.68867 9.13866 5.26234 9.13866 4.79477V1.11008ZM8.93547 0.758172C9.20638 0.601769 9.545 0.797268 9.545 1.11008V4.79477C9.545 5.41817 9.18845 5.98666 8.62724 6.25805L7.83111 6.64308C6.75178 7.16503 5.49805 6.37875 5.49805 5.1798V3.21199C5.49805 2.92162 5.65297 2.65335 5.90439 2.50817L8.93547 0.758172Z" fill="url(#paint17_linear_13399_1246)"/>
<path d="M15.4104 6.07479L16.2066 6.45982C17.151 6.91654 18.248 6.22851 18.248 5.17944V3.21163C18.248 2.99386 18.1318 2.79264 17.9432 2.68378L14.9121 0.933773C14.7767 0.855572 14.6074 0.953324 14.6074 1.10973V4.79443C14.6074 5.33991 14.9193 5.83731 15.4104 6.07479Z" fill="url(#paint18_linear_13399_1246)" stroke="url(#paint19_linear_13399_1246)" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1621 1.38378C11.665 1.09343 12.2846 1.09343 12.7875 1.38378L19.2559 5.11838C19.7589 5.40871 20.0686 5.9453 20.0686 6.526V13.9951C20.0686 14.5759 19.7589 15.1125 19.2559 15.4028L12.7875 19.1374C12.2846 19.4278 11.665 19.4278 11.1621 19.1374L4.69357 15.4028C4.19068 15.1125 3.88086 14.5759 3.88086 13.9951V6.526C3.88086 5.9453 4.19068 5.40871 4.69357 5.11838L11.1621 1.38378Z" fill="#AF711D"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.1621 0.926747C11.665 0.636397 12.2846 0.636397 12.7875 0.926747L19.2559 4.66134C19.7589 4.95167 20.0686 5.48829 20.0686 6.06897V13.5381C20.0686 14.1189 19.7589 14.6555 19.2559 14.9458L12.7875 18.6804C12.2846 18.9708 11.665 18.9708 11.1621 18.6804L4.69357 14.9458C4.19068 14.6555 3.88086 14.1189 3.88086 13.5381V6.06897C3.88086 5.48829 4.19068 4.95167 4.69357 4.66134L11.1621 0.926747Z" fill="#FAA229"/>
<g style="mix-blend-mode:multiply" opacity="0.8">
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.3425 5.40986L12.5908 2.08524C12.2096 1.86482 11.7397 1.86482 11.3584 2.08524L5.60673 5.40986C5.22539 5.63027 4.99048 6.03765 4.99048 6.47848V8.87687L4.52418 9.57715C4.43334 9.71356 4.43334 9.89119 4.52418 10.0276L4.99048 10.7279V13.1277C4.99048 13.5686 5.22539 13.9759 5.60673 14.1964L11.3584 17.521C11.7397 17.7414 12.2096 17.7414 12.5908 17.521L18.3425 14.1964C18.7239 13.9759 18.9587 13.5686 18.9587 13.1277V10.7278L19.425 10.0276C19.5159 9.89119 19.5159 9.71356 19.425 9.57715L18.9587 8.87695V6.47848C18.9587 6.03765 18.7239 5.63027 18.3425 5.40986ZM5.40129 10.7278V13.1277C5.40129 13.4216 5.55789 13.6932 5.81212 13.8401L11.5637 17.1647C11.818 17.3117 12.1312 17.3117 12.3855 17.1647L18.1371 13.8401C18.3913 13.6932 18.5479 13.4216 18.5479 13.1277V10.7278L18.0816 10.0276C17.9908 9.89119 17.9908 9.71356 18.0816 9.57715L18.5479 8.8769V6.47848C18.5479 6.18458 18.3913 5.91302 18.1371 5.76607L12.3855 2.44144C12.1312 2.2945 11.818 2.2945 11.5637 2.44144L5.81212 5.76607C5.55789 5.91302 5.40129 6.18458 5.40129 6.47848V8.87692L5.86756 9.57715C5.9584 9.71356 5.9584 9.89119 5.86756 10.0276L5.40129 10.7278Z" fill="#D9D9D9"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5557 3.56585C11.8386 3.40253 12.1871 3.40253 12.47 3.56585L17.1862 6.28877C17.4691 6.45209 17.6434 6.75394 17.6434 7.08058V12.5264C17.6434 12.8531 17.4691 13.155 17.1862 13.3183L12.47 16.0411C12.1871 16.2044 11.8386 16.2044 11.5557 16.0411L6.83945 13.3183C6.55659 13.155 6.38232 12.8531 6.38232 12.5264V7.08058C6.38232 6.75394 6.55659 6.45209 6.83945 6.28877L11.5557 3.56585Z" fill="url(#paint20_linear_13399_1246)"/>
<mask id="mask3_13399_1246" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="6" y="3" width="12" height="14">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.6066 3.53663C11.858 3.39143 12.1677 3.39143 12.4193 3.53663L17.2371 6.31821C17.4885 6.46339 17.6434 6.73168 17.6434 7.02201V12.5852C17.6434 12.8755 17.4885 13.1439 17.2371 13.289L12.4193 16.0706C12.1677 16.2157 11.858 16.2157 11.6066 16.0706L6.78867 13.289C6.53722 13.1439 6.38232 12.8755 6.38232 12.5852V7.02201C6.38232 6.73168 6.53722 6.46339 6.78867 6.31821L11.6066 3.53663Z" fill="white"/>
</mask>
<g mask="url(#mask3_13399_1246)">
<g style="mix-blend-mode:multiply" opacity="0.3">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9747 9.80336L5.47314 11.4288V8.17796L11.9747 9.80336ZM11.9747 9.80336L13.6001 3.30176H10.3493L11.9747 9.80336ZM11.9747 9.80336L18.4763 8.17796V11.4288L11.9747 9.80336ZM11.9747 9.80336L15.4227 4.0567L17.7214 6.35537L11.9747 9.80336ZM11.9747 9.80336L15.4227 15.55L17.7214 13.2514L11.9747 9.80336ZM11.9747 9.80336L10.3493 16.305H13.6001L11.9747 9.80336ZM11.9747 9.80336L8.52674 15.55L6.22809 13.2514L11.9747 9.80336Z" fill="#FAA229"/>
<path d="M8.5267 4.05664L6.22803 6.35529L11.9747 9.80327L8.5267 4.05664Z" fill="#FAA229"/>
</g>
</g>
<path d="M9.44629 7.59135L11.9747 6.07812L14.503 7.59135V12.0147L11.9747 13.5278L9.44629 12.0147V7.59135Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.6333 7.59156C8.6333 7.30577 8.78342 7.04098 9.02865 6.89422L11.5571 5.38097C11.814 5.22718 12.1348 5.22718 12.3917 5.38097L14.9202 6.89422C15.1654 7.04098 15.3155 7.30577 15.3155 7.59156V12.0149C15.3155 12.3006 15.1654 12.5654 14.9202 12.7121L12.3917 14.2254C12.1348 14.3792 11.814 14.3792 11.5571 14.2254L9.02865 12.7121C8.78342 12.5654 8.6333 12.3006 8.6333 12.0149V7.59156ZM11.9745 6.07834L9.44601 7.59156V12.0149L11.9745 13.528L14.5028 12.0149V7.59156L11.9745 6.07834Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.44629 7.59135L11.9747 6.07812V7.70775L10.8254 8.40617L9.44629 7.59135Z" fill="url(#paint21_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.44629 7.5918V12.0151L10.8254 11.2003V8.40662L9.44629 7.5918Z" fill="url(#paint22_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9747 13.5282L9.44629 12.015L10.8254 11.2002L11.9747 11.8986V13.5282Z" fill="url(#paint23_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9746 13.5282L14.503 12.015L13.1239 11.2002L11.9746 11.8986V13.5282Z" fill="url(#paint24_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M14.5031 7.5918V12.0151L13.124 11.2003V8.40662L14.5031 7.5918Z" fill="url(#paint25_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M14.503 7.59135L11.9746 6.07812V7.70775L13.1239 8.40617L14.503 7.59135Z" fill="url(#paint26_linear_13399_1246)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M10.8252 11.2001L11.9745 11.8985L13.1237 11.2001V8.40643L11.9745 7.70801L10.8252 8.40643V11.2001Z" fill="url(#paint27_linear_13399_1246)"/>
</g>
<defs>
<linearGradient id="paint0_linear_13399_1246" x1="0.571299" y1="2.89553" x2="0.571299" y2="11.4289" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint1_linear_13399_1246" x1="0.571247" y1="2.89507" x2="0.571247" y2="11.4284" gradientUnits="userSpaceOnUse">
<stop stop-color="#FDD194"/>
<stop offset="1" stop-color="#FFF6EA"/>
</linearGradient>
<linearGradient id="paint2_linear_13399_1246" x1="0.583994" y1="2.89553" x2="0.583994" y2="11.4289" gradientUnits="userSpaceOnUse">
<stop stop-color="#F1EDFA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint3_linear_13399_1246" x1="0.583944" y1="2.89507" x2="0.583944" y2="11.4284" gradientUnits="userSpaceOnUse">
<stop stop-color="#CCBCEA"/>
<stop offset="1" stop-color="#F1EDFA"/>
</linearGradient>
<linearGradient id="paint4_linear_13399_1246" x1="0.380861" y1="5.12989" x2="0.380861" y2="7.77126" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint5_linear_13399_1246" x1="0.787109" y1="7.56837" x2="0.787109" y2="10.2097" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint6_linear_13399_1246" x1="23.3522" y1="2.89553" x2="23.3522" y2="11.4289" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint7_linear_13399_1246" x1="23.3522" y1="2.89507" x2="23.3522" y2="11.4284" gradientUnits="userSpaceOnUse">
<stop stop-color="#FDD194"/>
<stop offset="1" stop-color="#FFF6EA"/>
</linearGradient>
<linearGradient id="paint8_linear_13399_1246" x1="23.0997" y1="2.89553" x2="23.0997" y2="11.4289" gradientUnits="userSpaceOnUse">
<stop stop-color="#F1EDFA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint9_linear_13399_1246" x1="23.0997" y1="2.89553" x2="23.0997" y2="11.4289" gradientUnits="userSpaceOnUse">
<stop stop-color="#CCBCEA"/>
<stop offset="1" stop-color="#F1EDFA"/>
</linearGradient>
<linearGradient id="paint10_linear_13399_1246" x1="23.5427" y1="5.12989" x2="23.5427" y2="7.77126" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint11_linear_13399_1246" x1="23.1366" y1="7.56837" x2="23.1366" y2="10.2097" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint12_linear_13399_1246" x1="2.62856" y1="12.2412" x2="2.62856" y2="16.1015" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint13_linear_13399_1246" x1="2.62871" y1="12.2413" x2="2.62871" y2="16.1016" gradientUnits="userSpaceOnUse">
<stop stop-color="#FDD194"/>
<stop offset="1" stop-color="#FFF6EA"/>
</linearGradient>
<linearGradient id="paint14_linear_13399_1246" x1="21.3207" y1="12.2412" x2="21.3207" y2="16.1015" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint15_linear_13399_1246" x1="21.3205" y1="12.2413" x2="21.3205" y2="16.1016" gradientUnits="userSpaceOnUse">
<stop stop-color="#FDD194"/>
<stop offset="1" stop-color="#FFF6EA"/>
</linearGradient>
<linearGradient id="paint16_linear_13399_1246" x1="5.49801" y1="0.703311" x2="5.49801" y2="6.93336" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA" stop-opacity="0.964706"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint17_linear_13399_1246" x1="5.49807" y1="0.703663" x2="5.49807" y2="6.93372" gradientUnits="userSpaceOnUse">
<stop stop-color="#FDD194"/>
<stop offset="1" stop-color="#FFF6EA"/>
</linearGradient>
<linearGradient id="paint18_linear_13399_1246" x1="18.4512" y1="0.703311" x2="18.4512" y2="6.93336" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint19_linear_13399_1246" x1="18.4512" y1="0.703311" x2="18.4512" y2="6.93336" gradientUnits="userSpaceOnUse">
<stop stop-color="#FDD194"/>
<stop offset="1" stop-color="#FFF6EA"/>
</linearGradient>
<linearGradient id="paint20_linear_13399_1246" x1="12.0129" y1="3.30192" x2="12.0129" y2="16.3051" gradientUnits="userSpaceOnUse">
<stop stop-color="#FFF6EA"/>
<stop offset="1" stop-color="#FDD194"/>
</linearGradient>
<linearGradient id="paint21_linear_13399_1246" x1="12.1657" y1="7.68437" x2="11.4773" y2="6.39334" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEE3BF"/>
<stop offset="1" stop-color="#FCBE69"/>
</linearGradient>
<linearGradient id="paint22_linear_13399_1246" x1="10.8254" y1="12.0151" x2="10.8254" y2="7.5918" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEE3BF"/>
<stop offset="1" stop-color="#FAA229"/>
</linearGradient>
<linearGradient id="paint23_linear_13399_1246" x1="11.3626" y1="13.0359" x2="11.9178" y2="11.7988" gradientUnits="userSpaceOnUse">
<stop stop-color="#FAA229"/>
<stop offset="1" stop-color="#FEE3BF"/>
</linearGradient>
<linearGradient id="paint24_linear_13399_1246" x1="14.503" y1="13.5282" x2="14.503" y2="11.2002" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEE3BF"/>
<stop offset="1" stop-color="#FAA229"/>
</linearGradient>
<linearGradient id="paint25_linear_13399_1246" x1="14.5031" y1="12.0151" x2="14.5031" y2="7.5918" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEE3BF"/>
<stop offset="1" stop-color="#FAA229"/>
</linearGradient>
<linearGradient id="paint26_linear_13399_1246" x1="13.1621" y1="8.37591" x2="13.7854" y2="7.16859" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEE3BF"/>
<stop offset="1" stop-color="#FAA229"/>
</linearGradient>
<linearGradient id="paint27_linear_13399_1246" x1="13.1237" y1="11.8985" x2="13.1237" y2="7.70801" gradientUnits="userSpaceOnUse">
<stop stop-color="#FEE3BF"/>
<stop offset="0.533654" stop-color="#FAA229"/>
</linearGradient>
<clipPath id="clip0_13399_1246">
<rect width="24" height="24" fill="white"/>
</clipPath>
</defs>
</svg>`,
            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<g clip-path="url(#clip0_13399_1390)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M7.14746 10.2734H13.6491V22.6192C13.6491 22.9212 13.3311 23.1177 13.061 22.9826L10.3983 21.6512L7.73553 22.9826C7.46536 23.1177 7.14746 22.9212 7.14746 22.6192V10.2734Z" fill="#6F58A4"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M10.3984 10.2734H16.9V22.6192C16.9 22.9212 16.5822 23.1177 16.312 22.9826L13.6493 21.6512L10.9865 22.9826C10.7163 23.1177 10.3984 22.9212 10.3984 22.6192V10.2734Z" fill="#6F58A4"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.52246 9.05371H12.0241V21.3994C12.0241 21.7014 11.7061 21.8979 11.436 21.7628L8.77326 20.4315L6.11053 21.7628C5.84034 21.8979 5.52246 21.7014 5.52246 21.3994V9.05371Z" fill="#6F58A4"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.0239 9.05371H18.5255V21.3994C18.5255 21.7014 18.2077 21.8979 17.9375 21.7628L15.2748 20.4315L12.612 21.7628C12.3419 21.8979 12.0239 21.7014 12.0239 21.3994V9.05371Z" fill="#6F58A4"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.52246 8.59668H12.0241V20.9424C12.0241 21.2444 11.7061 21.4409 11.436 21.3058L8.77326 19.9745L6.11053 21.3058C5.84034 21.4409 5.52246 21.2444 5.52246 20.9424V8.59668Z" fill="#53427B"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M7.14746 9.81543H13.6491V22.1611C13.6491 22.4632 13.3311 22.6597 13.061 22.5246L10.3983 21.1932L7.73553 22.5246C7.46536 22.6597 7.14746 22.4632 7.14746 22.1611V9.81543Z" fill="#8B6ECD"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.0239 8.59668H18.5255V20.9424C18.5255 21.2444 18.2077 21.4409 17.9375 21.3058L15.2748 19.9745L12.612 21.3058C12.3419 21.4409 12.0239 21.2444 12.0239 20.9424V8.59668Z" fill="#53427B"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M10.3984 9.81543H16.9V22.1611C16.9 22.4632 16.5822 22.6597 16.312 22.5246L13.6493 21.1932L10.9865 22.5246C10.7163 22.6597 10.3984 22.4632 10.3984 22.1611V9.81543Z" fill="#8B6ECD"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.77295 11.0859H15.2745V23.4317C15.2745 23.7337 14.9566 23.9302 14.6864 23.7951L12.0238 22.4638L9.36102 23.7951C9.09083 23.9302 8.77295 23.7337 8.77295 23.4317V11.0859Z" fill="#6F58A4"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.77295 10.6289H15.2745V22.9746C15.2745 23.2767 14.9566 23.4732 14.6864 23.3381L12.0238 22.0068L9.36102 23.3381C9.09083 23.4732 8.77295 23.2767 8.77295 22.9746V10.6289Z" fill="white"/>
<mask id="mask0_13399_1390" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="8" y="10" width="8" height="14">
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.77295 10.6289H15.2745V22.9746C15.2745 23.2767 14.9566 23.4732 14.6864 23.3381L12.0238 22.0068L9.36102 23.3381C9.09083 23.4732 8.77295 23.2767 8.77295 22.9746V10.6289Z" fill="white"/>
</mask>
<g mask="url(#mask0_13399_1390)">
<path d="M13.6492 10.6289H10.3984V23.6321H13.6492V10.6289Z" fill="#8B6ECD"/>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.1554 4.88336L3.12365 4.07067V3.66433L1.17253 2.85135C0.980632 2.77141 0.783124 2.95287 0.846573 3.15083L3.16109 10.3721C3.20132 10.4976 3.29996 10.5959 3.42567 10.6355L5.1554 11.1818V4.88336Z" fill="url(#paint0_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M0.653044 3.21337C0.536716 2.85042 0.898835 2.51775 1.25062 2.66434L3.20174 3.47732C3.27746 3.50886 3.32678 3.58282 3.32678 3.66486V3.93363L5.2308 4.69525C5.30793 4.72611 5.35852 4.80082 5.35852 4.88389V11.1823C5.35852 11.2471 5.32766 11.3079 5.27544 11.3462C5.22324 11.3845 5.15589 11.3955 5.09416 11.3761L3.36446 10.8298C3.17589 10.7703 3.02791 10.623 2.96755 10.4346M2.96755 10.4346L0.653044 3.21337L2.96755 10.4346ZM1.09433 3.03944C1.06234 3.0261 1.02943 3.05636 1.04 3.08934L3.35452 10.3106C3.37464 10.3734 3.42396 10.4225 3.48681 10.4423L4.95218 10.9051V5.02146L3.04814 4.25984C2.971 4.22898 2.92041 4.15429 2.92041 4.0712V3.80031L1.09433 3.03944Z" fill="url(#paint1_linear_13399_1390)"/>
<mask id="mask1_13399_1390" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="2" width="6" height="10">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.1554 4.88434L3.12365 4.07165V3.66531L1.17253 2.85233C0.980632 2.77239 0.783124 2.95385 0.846573 3.1518L3.16109 10.3731C3.20132 10.4986 3.29996 10.5968 3.42567 10.6365L5.1554 11.1828V4.88434Z" fill="url(#paint2_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M0.653044 3.21337C0.536716 2.85042 0.898835 2.51775 1.25062 2.66434L3.20174 3.47732C3.27746 3.50886 3.32678 3.58282 3.32678 3.66486V3.93363L5.2308 4.69525C5.30793 4.72611 5.35852 4.80082 5.35852 4.88389V11.1823C5.35852 11.2471 5.32766 11.3079 5.27544 11.3462C5.22324 11.3845 5.15589 11.3955 5.09416 11.3761L3.36446 10.8298C3.17589 10.7703 3.02791 10.623 2.96755 10.4346M2.96755 10.4346L0.653044 3.21337L2.96755 10.4346ZM1.09433 3.03944C1.06234 3.0261 1.02943 3.05636 1.04 3.08934L3.35452 10.3106C3.37464 10.3734 3.42396 10.4225 3.48681 10.4423L4.95218 10.9051V5.02146L3.04814 4.25984C2.971 4.22898 2.92041 4.15429 2.92041 4.0712V3.80031L1.09433 3.03944Z" fill="url(#paint3_linear_13399_1390)"/>
</mask>
<g mask="url(#mask1_13399_1390)">
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M5.23095 7.13329C5.33514 7.17496 5.3858 7.2932 5.34414 7.39739C5.30245 7.50157 5.18422 7.55226 5.08004 7.51057L3.0483 6.69788C2.97116 6.66702 2.92057 6.59231 2.92057 6.50924V6.23833L0.607506 5.27456C0.503927 5.23139 0.454945 5.11244 0.498104 5.00886C0.541261 4.9053 0.660215 4.85632 0.763792 4.89947L3.2019 5.91534C3.27759 5.94688 3.32691 6.02087 3.32691 6.10288V6.37168L5.23095 7.13329Z" fill="url(#paint4_linear_13399_1390)"/>
</g>
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M5.63719 9.57178C5.7414 9.61345 5.79206 9.73169 5.75037 9.83588C5.70871 9.94006 5.59049 9.99073 5.48628 9.94906L3.45453 9.13635C3.3774 9.10551 3.32681 9.0308 3.32681 8.94771V8.67682L1.01375 7.71305C0.910178 7.6699 0.861196 7.55093 0.904353 7.44735C0.947512 7.34377 1.06647 7.29478 1.17004 7.33796L3.60813 8.35383C3.68385 8.38537 3.73317 8.45936 3.73317 8.54137V8.81017L5.63719 9.57178Z" fill="url(#paint5_linear_13399_1390)"/>
</g>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M19 4.96541L20.8808 4.18676V3.79744L22.6871 3.01851C22.8647 2.94192 23.0475 3.11578 22.9888 3.30544L20.8462 10.2242C20.809 10.3445 20.7176 10.4386 20.6012 10.4766L19 11V4.96541Z" fill="url(#paint6_linear_13399_1390)" stroke="url(#paint7_linear_13399_1390)" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
<mask id="mask2_13399_1390" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="18" y="2" width="6" height="10">
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.8442 4.88434L20.876 4.07165V3.66531L22.8271 2.85233C23.019 2.77239 23.2165 2.95385 23.153 3.1518L20.8385 10.3731C20.7983 10.4986 20.6997 10.5968 20.5739 10.6365L18.8442 11.1828V4.88434Z" fill="url(#paint8_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M22.9596 3.08933C22.9702 3.05631 22.9372 3.02612 22.9053 3.03944L21.0792 3.80031V4.0712C21.0792 4.15428 21.0287 4.229 20.9515 4.25984L19.0475 5.02145V10.9051L20.5128 10.4423C20.5757 10.4225 20.625 10.3734 20.6451 10.3106L22.9596 3.08933ZM22.749 2.66434C23.1008 2.51775 23.4628 2.85046 23.3466 3.21334L21.032 10.4346C20.9717 10.6229 20.8237 10.7703 20.6352 10.8298L18.9055 11.3761C18.8437 11.3955 18.7764 11.3845 18.7242 11.3462C18.672 11.3079 18.6411 11.2471 18.6411 11.1823V4.88388C18.6411 4.80082 18.6917 4.72611 18.7688 4.69524L20.6729 3.93365V3.66485C20.6729 3.58284 20.7222 3.50885 20.7979 3.47732L22.749 2.66434Z" fill="url(#paint9_linear_13399_1390)"/>
</mask>
<g mask="url(#mask2_13399_1390)">
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M18.7689 7.13329C18.6647 7.17496 18.6141 7.2932 18.6557 7.39739C18.6974 7.50157 18.8157 7.55226 18.9198 7.51057L20.9515 6.69788C21.0287 6.66702 21.0793 6.59231 21.0793 6.50924V6.23833L23.3923 5.27456C23.4959 5.23139 23.5449 5.11244 23.5017 5.00886C23.4585 4.9053 23.3397 4.85632 23.236 4.89947L20.7979 5.91534C20.7223 5.94688 20.6729 6.02087 20.6729 6.10288V6.37168L18.7689 7.13329Z" fill="url(#paint10_linear_13399_1390)"/>
</g>
<g style="mix-blend-mode:multiply" opacity="0.6">
<path d="M18.3626 9.57178C18.2584 9.61345 18.2078 9.73169 18.2495 9.83588C18.2911 9.94006 18.4094 9.99073 18.5136 9.94906L20.5452 9.13635C20.6224 9.10551 20.673 9.0308 20.673 8.94771V8.67682L22.986 7.71305C23.0896 7.6699 23.1386 7.55093 23.0954 7.44735C23.0523 7.34377 22.9334 7.29478 22.8298 7.33796L20.3916 8.35383C20.316 8.38537 20.2666 8.45936 20.2666 8.54137V8.81017L18.3626 9.57178Z" fill="url(#paint11_linear_13399_1390)"/>
</g>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.6603 11.9941L3.2275 12.7106C3.10705 12.7708 3.02425 12.8868 3.00644 13.0203L2.69367 15.3661C2.66701 15.5663 2.88106 15.7102 3.05631 15.61L5.47302 14.2291L4.6603 11.9941Z" fill="url(#paint12_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.73567 11.8055C4.7895 11.8269 4.83162 11.8703 4.85142 11.9248L5.66411 14.1597C5.6979 14.2527 5.65986 14.3565 5.57397 14.4056L3.15726 15.7865C2.83609 15.97 2.44353 15.7063 2.49244 15.3394L2.80521 12.9935C2.83192 12.7933 2.95609 12.6193 3.13679 12.5289L4.56959 11.8125C4.62143 11.7866 4.68184 11.784 4.73567 11.8055ZM4.54744 12.2779L3.31854 12.8924C3.2583 12.9225 3.2169 12.9805 3.208 13.0472M3.208 13.0472L2.89523 15.393C2.89079 15.4263 2.92636 15.4504 2.95566 15.4337L5.22371 14.1377L4.54744 12.2779" fill="url(#paint13_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M19.2888 11.9941L20.7216 12.7106C20.842 12.7708 20.9247 12.8868 20.9426 13.0203L21.2554 15.3661C21.282 15.5663 21.068 15.7102 20.8928 15.61L18.4761 14.2291L19.2888 11.9941Z" fill="url(#paint14_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M19.2136 11.8055C19.2675 11.784 19.3279 11.7866 19.3797 11.8125L20.8125 12.5289C20.9932 12.6193 21.1173 12.7933 21.144 12.9935L21.4569 15.3393C21.5057 15.7061 21.1133 15.97 20.792 15.7865L18.3754 14.4056C18.2895 14.3565 18.2514 14.2527 18.2852 14.1597L19.0979 11.9248C19.1177 11.8703 19.1598 11.8269 19.2136 11.8055ZM19.4019 12.2779L18.7256 14.1377L20.9936 15.4337C21.0228 15.4504 21.0585 15.4264 21.0541 15.393L20.7413 13.0473C20.7324 12.9804 20.6909 12.9225 20.6308 12.8924L19.4019 12.2779Z" fill="url(#paint15_linear_13399_1390)"/>
<path d="M8.53874 5.8287L7.74259 6.21371C6.79816 6.67045 5.70117 5.98242 5.70117 4.93336V2.96553C5.70117 2.74777 5.81736 2.54656 6.00595 2.43767L9.037 0.687679C9.17246 0.609478 9.34178 0.70723 9.34178 0.863633V4.54833C9.34178 5.0938 9.0298 5.59122 8.53874 5.8287Z" fill="url(#paint16_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.13866 0.863987L6.10757 2.61397C5.98186 2.68657 5.90439 2.82071 5.90439 2.96588V4.93372C5.90439 5.83293 6.84469 6.42265 7.65418 6.03116L8.45034 5.64613C8.87125 5.44259 9.13866 5.01623 9.13866 4.54869V0.863987ZM8.93547 0.512077C9.20638 0.355676 9.545 0.551175 9.545 0.863987V4.54869C9.545 5.17208 9.18845 5.74055 8.62724 6.01196L7.83111 6.39697C6.75178 6.91895 5.49805 6.13264 5.49805 4.93372V2.96588C5.49805 2.67553 5.65297 2.40724 5.90439 2.26207L8.93547 0.512077Z" fill="url(#paint17_linear_13399_1390)"/>
<path d="M15.4104 5.8287L16.2066 6.21371C17.151 6.67045 18.248 5.98242 18.248 4.93336V2.96553C18.248 2.74777 18.1318 2.54656 17.9432 2.43767L14.9121 0.687679C14.7767 0.609478 14.6074 0.70723 14.6074 0.863633V4.54833C14.6074 5.0938 14.9193 5.59122 15.4104 5.8287Z" fill="url(#paint18_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M15.0137 0.512084C15.0137 0.512081 15.0137 0.512086 15.0137 0.512084L18.0448 2.26208C18.2962 2.40723 18.4512 2.67551 18.4512 2.96588V4.93372C18.4512 6.13264 17.1975 6.91897 16.1182 6.397L15.322 6.01196C14.7607 5.74055 14.4043 5.17206 14.4043 4.54868V0.863993C14.4043 0.551266 14.7428 0.35564 15.0137 0.512084ZM14.8106 0.864039V4.54868C14.8106 5.01625 15.078 5.44259 15.4989 5.64613L16.2951 6.03116C17.1045 6.42265 18.0449 5.83293 18.0449 4.93372V2.96588C18.0449 2.82076 17.9674 2.6866 17.8416 2.614L14.8106 0.864039Z" fill="url(#paint19_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.2109 1.39647C11.7138 1.10612 12.3334 1.10612 12.8364 1.39647L19.3048 5.13107C19.8077 5.42142 20.1175 5.95801 20.1175 6.53869V14.0079C20.1175 14.5886 19.8077 15.1252 19.3048 15.4155L12.8364 19.1501C12.3334 19.4405 11.7138 19.4405 11.2109 19.1501L4.74238 15.4155C4.23948 15.1252 3.92969 14.5886 3.92969 14.0079V6.53869C3.92969 5.95801 4.23948 5.42142 4.74238 5.13107L11.2109 1.39647Z" fill="#6F58A4"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.2109 0.939442C11.7138 0.649092 12.3334 0.649092 12.8364 0.939442L19.3048 4.67403C19.8077 4.96438 20.1175 5.50098 20.1175 6.08168V13.5509C20.1175 14.1316 19.8077 14.6682 19.3048 14.9584L12.8364 18.6931C12.3334 18.9835 11.7138 18.9835 11.2109 18.6931L4.74238 14.9584C4.23948 14.6682 3.92969 14.1316 3.92969 13.5509V6.08168C3.92969 5.50098 4.23948 4.96438 4.74238 4.67403L11.2109 0.939442Z" fill="#8B6ECD"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.3174 5.40986L12.5657 2.08524C12.1844 1.86482 11.7145 1.86482 11.3332 2.08524L5.58158 5.40986C5.20024 5.63027 4.96533 6.03765 4.96533 6.47848V13.1277C4.96533 13.5686 5.20024 13.9759 5.58158 14.1964L11.3332 17.521C11.7145 17.7414 12.1844 17.7414 12.5657 17.521L18.3174 14.1964C18.6987 13.9759 18.9336 13.5686 18.9336 13.1277V6.47848C18.9336 6.03765 18.6987 5.63027 18.3174 5.40986ZM11.5387 2.44144C11.7928 2.2945 12.1061 2.2945 12.3603 2.44144L18.1119 5.76607C18.3661 5.91302 18.5228 6.18458 18.5228 6.47848V13.1277C18.5228 13.4216 18.3661 13.6932 18.1119 13.8401L12.3603 17.1647C12.1061 17.3117 11.7928 17.3117 11.5387 17.1647L5.78697 13.8401C5.53274 13.6932 5.37614 13.4216 5.37614 13.1277V6.47848C5.37614 6.18458 5.53274 5.91302 5.78697 5.76607L11.5387 2.44144Z" fill="#53427B"/>
<path d="M18.7281 8.56836L19.3998 9.57708C19.4906 9.7135 19.4906 9.89112 19.3998 10.0275L18.7281 11.0363L18.0564 10.0275C17.9656 9.89112 17.9656 9.7135 18.0564 9.57708L18.7281 8.56836Z" fill="#53427B"/>
<path d="M5.17048 8.56836L5.84217 9.57708C5.93301 9.7135 5.93301 9.89112 5.84217 10.0275L5.17048 11.0363L4.49879 10.0275C4.40795 9.89112 4.40795 9.7135 4.49879 9.57708L5.17048 8.56836Z" fill="#53427B"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.2922 3.73689C11.5586 3.58305 11.6919 3.50615 11.8335 3.47603C11.9588 3.44939 12.0883 3.44939 12.2137 3.47603C12.3553 3.50615 12.4885 3.58305 12.7551 3.73689L16.9227 6.14311C17.1891 6.29695 17.3224 6.37389 17.4193 6.48148C17.505 6.57671 17.5698 6.68889 17.6093 6.81074C17.6542 6.94847 17.6542 7.10231 17.6542 7.40999V12.2224C17.6542 12.53 17.6542 12.684 17.6093 12.8217C17.5698 12.9435 17.505 13.0556 17.4193 13.151C17.3224 13.2585 17.1891 13.3354 16.9227 13.4892L12.7551 15.8955C12.4885 16.0493 12.3553 16.1263 12.2137 16.1563C12.0883 16.183 11.9588 16.183 11.8335 16.1563C11.6919 16.1263 11.5586 16.0493 11.2922 15.8955L7.12449 13.4892C6.85802 13.3354 6.72479 13.2585 6.62788 13.151C6.54218 13.0556 6.4774 12.9435 6.4378 12.8217C6.39307 12.684 6.39307 12.53 6.39307 12.2224V7.40999C6.39307 7.10231 6.39307 6.94847 6.4378 6.81074C6.4774 6.68889 6.54218 6.57671 6.62788 6.48148C6.72479 6.37389 6.85802 6.29695 7.12449 6.14311L11.2922 3.73689Z" fill="url(#paint20_linear_13399_1390)"/>
<mask id="mask3_13399_1390" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="6" y="3" width="12" height="14">
<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5811 3.54931C11.8326 3.40414 12.1424 3.40414 12.3938 3.54931L17.2117 6.33089C17.4632 6.47606 17.618 6.74436 17.618 7.03471V12.5979C17.618 12.8882 17.4632 13.1565 17.2117 13.3017L12.3938 16.0833C12.1424 16.2284 11.8326 16.2284 11.5811 16.0833L6.76328 13.3017C6.51183 13.1565 6.35693 12.8882 6.35693 12.5979V7.03471C6.35693 6.74436 6.51183 6.47606 6.76328 6.33089L11.5811 3.54931Z" fill="white"/>
</mask>
<g mask="url(#mask3_13399_1390)">
<g style="mix-blend-mode:multiply" opacity="0.3">
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.0241 9.81603L5.52246 11.4414V8.19063L12.0241 9.81603ZM12.0241 9.81603L13.6495 3.31445H10.3987L12.0241 9.81603ZM12.0241 9.81603L18.5257 8.19063V11.4414L12.0241 9.81603ZM12.0241 9.81603L15.4721 4.06937L17.7707 6.36805L12.0241 9.81603ZM12.0241 9.81603L15.4721 15.5627L17.7707 13.264L12.0241 9.81603ZM12.0241 9.81603L10.3987 16.3177H13.6495L12.0241 9.81603ZM12.0241 9.81603L8.57605 15.5627L6.2774 13.264L12.0241 9.81603Z" fill="#8B6ECD"/>
<path d="M8.57599 4.06934L6.27734 6.36798L12.024 9.81599L8.57599 4.06934Z" fill="#8B6ECD"/>
</g>
</g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.3667 9.4093L9.58575 7.29785H14.462L15.6809 9.4093H8.3667ZM12.0238 13.0664L15.6809 9.4093H8.3667L12.0238 13.0664Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.88167 6.8917C9.02683 6.64025 9.29512 6.48535 9.58547 6.48535H14.4617C14.752 6.48535 15.0203 6.64025 15.1654 6.8917L16.3815 8.99786C16.3894 9.01126 16.3969 9.02494 16.404 9.03888C16.4086 9.04786 16.413 9.05688 16.4172 9.06598C16.4813 9.20342 16.505 9.35446 16.4882 9.50194C16.481 9.5646 16.4665 9.62664 16.4446 9.68671C16.4135 9.7727 16.3676 9.85394 16.3078 9.92642C16.302 9.93346 16.2962 9.94037 16.2901 9.94718C16.2779 9.96108 16.2651 9.97452 16.2521 9.9875L12.5983 13.6414C12.4458 13.7937 12.2391 13.8793 12.0235 13.8793C11.8081 13.8793 11.6014 13.7937 11.4489 13.6414L7.7951 9.98748C7.77739 9.96998 7.76042 9.95162 7.74429 9.93242C7.68206 9.85843 7.63449 9.77508 7.60245 9.68671C7.54499 9.52824 7.53741 9.35354 7.58464 9.18744C7.59782 9.141 7.61522 9.0955 7.63684 9.05146C7.64582 9.03317 7.65544 9.01529 7.66566 8.99786L8.88167 6.8917ZM8.83564 8.5968L8.36644 9.40951L12.0235 13.0666L15.6807 9.40951L15.2115 8.5968L14.4617 7.29804H9.58547L8.83564 8.5968Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.0238 13.0663L15.6809 9.40918H8.3667L12.0238 13.0663Z" fill="url(#paint21_linear_13399_1390)"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.0238 13.0663L10.8047 9.40918H13.2428L12.0238 13.0663Z" fill="url(#paint22_linear_13399_1390)"/>
<path d="M9.58575 7.29785L8.3667 9.4093H15.6809L14.462 7.29785H9.58575Z" fill="url(#paint23_linear_13399_1390)"/>
<path d="M9.58575 7.29785L10.8048 9.4093H8.3667L9.58575 7.29785Z" fill="url(#paint24_linear_13399_1390)"/>
<path d="M12.0238 7.29785L13.2428 9.4093H10.8047L12.0238 7.29785Z" fill="url(#paint25_linear_13399_1390)"/>
<path d="M14.4618 7.29785L15.6807 9.4093H13.2427L14.4618 7.29785Z" fill="url(#paint26_linear_13399_1390)"/>
</g>
<defs>
<linearGradient id="paint0_linear_13399_1390" x1="0.685554" y1="2.64846" x2="0.685554" y2="11.1818" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint1_linear_13399_1390" x1="0.685506" y1="2.64898" x2="0.685506" y2="11.1823" gradientUnits="userSpaceOnUse">
<stop stop-color="#C5B7E6"/>
<stop offset="1" stop-color="#F3F1FA"/>
</linearGradient>
<linearGradient id="paint2_linear_13399_1390" x1="0.685554" y1="2.64943" x2="0.685554" y2="11.1828" gradientUnits="userSpaceOnUse">
<stop stop-color="#F1EDFA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint3_linear_13399_1390" x1="0.685506" y1="2.64898" x2="0.685506" y2="11.1823" gradientUnits="userSpaceOnUse">
<stop stop-color="#CCBCEA"/>
<stop offset="1" stop-color="#F1EDFA"/>
</linearGradient>
<linearGradient id="paint4_linear_13399_1390" x1="0.48242" y1="4.8838" x2="0.48242" y2="7.52516" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint5_linear_13399_1390" x1="0.888671" y1="7.32226" x2="0.888671" y2="9.96365" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint6_linear_13399_1390" x1="23.1378" y1="2.82412" x2="23.1378" y2="11" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint7_linear_13399_1390" x1="23.1378" y1="2.82412" x2="23.1378" y2="11" gradientUnits="userSpaceOnUse">
<stop stop-color="#C5B7E6"/>
<stop offset="1" stop-color="#F3F1FA"/>
</linearGradient>
<linearGradient id="paint8_linear_13399_1390" x1="23.314" y1="2.64943" x2="23.314" y2="11.1828" gradientUnits="userSpaceOnUse">
<stop stop-color="#F1EDFA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint9_linear_13399_1390" x1="23.3141" y1="2.64898" x2="23.3141" y2="11.1823" gradientUnits="userSpaceOnUse">
<stop stop-color="#CCBCEA"/>
<stop offset="1" stop-color="#F1EDFA"/>
</linearGradient>
<linearGradient id="paint10_linear_13399_1390" x1="23.5174" y1="4.8838" x2="23.5174" y2="7.52516" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint11_linear_13399_1390" x1="23.1112" y1="7.32226" x2="23.1112" y2="9.96365" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#CCBCEA"/>
</linearGradient>
<linearGradient id="paint12_linear_13399_1390" x1="2.62856" y1="11.9941" x2="2.62856" y2="15.8545" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint13_linear_13399_1390" x1="2.62871" y1="11.9942" x2="2.62871" y2="15.8545" gradientUnits="userSpaceOnUse">
<stop stop-color="#C5B7E6"/>
<stop offset="1" stop-color="#F3F1FA"/>
</linearGradient>
<linearGradient id="paint14_linear_13399_1390" x1="21.3205" y1="11.9941" x2="21.3205" y2="15.8545" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint15_linear_13399_1390" x1="21.3206" y1="11.9942" x2="21.3206" y2="15.8545" gradientUnits="userSpaceOnUse">
<stop stop-color="#C5B7E6"/>
<stop offset="1" stop-color="#F3F1FA"/>
</linearGradient>
<linearGradient id="paint16_linear_13399_1390" x1="5.49801" y1="0.457217" x2="5.49801" y2="6.68728" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint17_linear_13399_1390" x1="5.49807" y1="0.457571" x2="5.49807" y2="6.68764" gradientUnits="userSpaceOnUse">
<stop stop-color="#C5B7E6"/>
<stop offset="1" stop-color="#F3F1FA"/>
</linearGradient>
<linearGradient id="paint18_linear_13399_1390" x1="18.4512" y1="0.457217" x2="18.4512" y2="6.68728" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint19_linear_13399_1390" x1="18.4512" y1="0.457575" x2="18.4512" y2="6.68764" gradientUnits="userSpaceOnUse">
<stop stop-color="#C5B7E6"/>
<stop offset="1" stop-color="#F3F1FA"/>
</linearGradient>
<linearGradient id="paint20_linear_13399_1390" x1="12.0239" y1="3.05903" x2="12.0239" y2="17.4071" gradientUnits="userSpaceOnUse">
<stop stop-color="#F3F1FA"/>
<stop offset="1" stop-color="#C5B7E6"/>
</linearGradient>
<linearGradient id="paint21_linear_13399_1390" x1="12.0238" y1="13.0663" x2="12.0238" y2="9.40918" gradientUnits="userSpaceOnUse">
<stop stop-color="#878787"/>
<stop offset="1" stop-color="#1F1F1F"/>
</linearGradient>
<linearGradient id="paint22_linear_13399_1390" x1="13.2428" y1="13.0663" x2="13.2428" y2="9.40918" gradientUnits="userSpaceOnUse">
<stop stop-color="#878787"/>
<stop offset="1" stop-color="#4E4E4E"/>
</linearGradient>
<linearGradient id="paint23_linear_13399_1390" x1="15.6809" y1="9.4093" x2="15.6809" y2="7.29785" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="#A8A8A8"/>
</linearGradient>
<linearGradient id="paint24_linear_13399_1390" x1="9.17941" y1="9.4093" x2="9.17941" y2="7.64976" gradientUnits="userSpaceOnUse">
<stop stop-color="#818181"/>
<stop offset="1" stop-color="#A0A0A0"/>
</linearGradient>
<linearGradient id="paint25_linear_13399_1390" x1="11.6174" y1="9.4093" x2="11.6174" y2="7.64976" gradientUnits="userSpaceOnUse">
<stop stop-color="#818181"/>
<stop offset="1" stop-color="#A0A0A0"/>
</linearGradient>
<linearGradient id="paint26_linear_13399_1390" x1="14.0554" y1="9.4093" x2="14.0554" y2="7.64976" gradientUnits="userSpaceOnUse">
<stop stop-color="#818181"/>
<stop offset="1" stop-color="#A0A0A0"/>
</linearGradient>
<clipPath id="clip0_13399_1390">
<rect width="24" height="24" fill="white"/>
</clipPath>
</defs>
</svg>`,
        ];

        return svgs[index % svgs.length];
    }

    #hideGoalSelectUi(wrapper) {
        if (!(wrapper instanceof HTMLElement)) {
            return;
        }

        wrapper.querySelectorAll('select, .choices, .choices__inner').forEach((node) => {
            if (node instanceof HTMLElement) {
                node.style.setProperty('display', 'none', 'important');
            }
        });
    }

    #getGoalButtonLabel(option) {
        if (!(option instanceof HTMLOptionElement)) {
            return '';
        }

        const rawValue = option.value || '';
        if (rawValue.includes('--')) {
            const amount = Number.parseFloat(rawValue.split('--')[0]);
            if (!Number.isNaN(amount)) {
                const formatted = amount.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
                });
                return `$${formatted}`;
            }
        }

        return this.#formatGoalButtonLabel(option.textContent?.trim() || rawValue);
    }

    #formatGoalButtonLabel(label = '') {
        if (typeof label !== 'string' || !label.trim()) {
            return label;
        }

        const withCommas = label.replace(/\d{1,3}(?:,\d{3})+(?:\.\d+)?/g, (match) => {
            const normalized = Number.parseFloat(match.replace(/,/g, ''));
            if (Number.isNaN(normalized)) {
                return match;
            }
            return normalized.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: match.includes('.') ? 2 : 0,
            });
        });

        return withCommas.replace(/\b\d+(?:\.\d+)?\b/g, (match) => {
            const normalized = Number.parseFloat(match);
            if (Number.isNaN(normalized)) {
                return match;
            }
            return normalized.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: match.includes('.') ? 2 : 0,
            });
        });
    }

    #buildWalkAndFundraiseToggle() {
        const wrapper = document.createElement('div');
        wrapper.className = 'form-group section-header walk-fundraise-mode-toggle fundraise-mode-toggle';
        wrapper.innerHTML = `
            <div class="section-header-inner">
                <div class="section-header-left">
                    <span class="group-title first">I am walking and fundraising</span>
                </div>
                <div class="section-header-right">
                    <label class="toggle-item">
                        <input type="checkbox" name="walk_fundraise_mode" value="1">
                        <span class="toggle-switch" aria-hidden="true"></span>
                    </label>
                </div>
            </div>
        `;

        return wrapper;
    }

    #initTeamSearch() {
        const select = this.form.querySelector('select[name="team_id"]');
        const selectWrapper = select?.closest('.levcharity_select_wrapper');

        if (!select || !selectWrapper) return;

        const urlTeamId = new URLSearchParams(window.location.search).get('team_id');
        if (urlTeamId) {
            select.value = urlTeamId;
        }

        const syncPreselectedTeam = () => {
            const teamId = select.value;
            if (!teamId) {
                return false;
            }

            const selectedOption = select.options[select.selectedIndex];
            const choicesInstance = select.choices_instance || select.choicesInstance;

            if (choicesInstance?.setChoiceByValue) {
                choicesInstance.setChoiceByValue(teamId);
            } else if (!select.closest('.choices')) {
                return false;
            }

            const teamName = selectedOption?.textContent?.trim() || '';
            const teamImageSrc = selectedOption?.getAttribute('data-image-src') || '';

            if (teamName) {
                this.#saveTeamCardData(teamName, teamImageSrc, teamId);
            }

            return true;
        };

        if (!syncPreselectedTeam()) {
            const observer = new MutationObserver(() => {
                if (syncPreselectedTeam()) {
                    observer.disconnect();
                }
            });

            observer.observe(select.parentElement, {childList: true, subtree: true});
            setTimeout(() => {
                observer.disconnect();
                syncPreselectedTeam();
            }, 2000);
        }

        window.addEventListener('pageshow', () => selectWrapper.classList.remove('disabled'));

        select.addEventListener('change', (e) => {
            const selectedOption = e.target.options[e.target.selectedIndex];
            const teamName = selectedOption?.textContent.trim() || '';
            const teamImageSrc = selectedOption?.getAttribute('data-image-src') || '';
            const teamId = e.target.value;

            this.#saveTeamCardData(teamName, teamImageSrc, teamId);

            let baseUrl = window.location.origin + window.location.pathname;
            if (baseUrl.endsWith('/')) {
                baseUrl = baseUrl.slice(0, -1);
            }
            baseUrl = baseUrl.replace(/\/join-team\/?$/, '');

            const newUrl = `${baseUrl}/join-team?team_id=${teamId}`;
            selectWrapper.classList.add('disabled');
            window.location.href = newUrl;
        });
    }

    #saveTeamCardData(teamName = '', src = '', id = 0) {
        const cookieValue = JSON.stringify({
            name: teamName,
            image_src: src,
            id: id,
            action: 'join-team',
        });

        document.cookie = `team_card_data=${encodeURIComponent(cookieValue)}; path=/;`;
    }
}
